<template>
  <div id="voicemails" class="flex flex-col gap-6 text_color">
    <div
      id="voicemails_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">Voicemails</h1>
      <a-button class="primary_medium" @click="handleAddVoicemail"
        >Add Voicemail</a-button
      >
    </div>

    <div id="voicemails_body">
      <SkeletonLoader v-if="loadingTableData" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="voicemails?.length > 0"
        :headers="voicemailTableHeaders"
        :data="voicemails"
        :show-actions-column="true"
        :actions="actions"
        :custom-column-render="customColumnRender"
      />

      <EmptyScreen v-else source="voice_mails" text="No voicemails found!" />
    </div>

    <div id="voicemails_components">
      <add-voicemail
        source="voicemails"
        v-model:visible="showAddModal"
        @update:success="handleFetchVoicemails"
      />

      <CustomModal
        title="Edit Voicemail Name"
        v-model:visible="showEditModal"
        width="32vw"
      >
        <div class="form_col">
          <span class="text-sm secondary_color">Voicemail Name</span>
          <a-input
            v-model:value="editableName"
            placeholder="Voicemail name"
            class="input_medium w-full"
            ref="editVoicemailNameInputRef"
            allow-clear
          />
        </div>

        <template #footer>
          <a-button class="primary_medium" @click="toUpdateVoicemail"
            >Confirm</a-button
          >
        </template>
      </CustomModal>

      <CustomModal
        title="Delete Voicemail"
        v-model:visible="showDeleteModal"
        width="32vw"
      >
        <span class="text-base text_color leading-5"
          >Are you sure you want to delete
          <b>{{ voicemailToEditOrDelete.name }}</b> voicemail?</span
        >
        <template #footer>
          <a-button class="primary_medium" @click="toDeleteVoicemail"
            >Confirm</a-button
          >
        </template>
      </CustomModal>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { voicemailTableHeaders } from "@/src/constants/callConstants.js";
import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import AddVoicemail from "@/src/views/calls/voicemails/AddVoicemail.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import AudioPlayer from "@/src/components/AudioPlayer.vue";
import { toFetchVoicemails } from "@/src/utils/callsUtils.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {imageNames} from "@/src/constants/imageConstants.js";

const editVoicemailNameInputRef = ref(null);
const voicemails = ref(null);
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const editableName = ref("");
const voicemailToEditOrDelete = ref(null);
const loadingTableData = ref(false);

const actions = computed(() => [
  {
    label: "Edit name",
    handler: (row) => handleEditRow(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => handleDeleteClick(row),
    icon: imageNames.DELETE_GREY,
  },
]);

const customColumnRender = {
  source: AudioPlayer,
};

const handleAddVoicemail = () => {
  showAddModal.value = true;
};

const handleEditRow = (row) => {
  showEditModal.value = true;
  editableName.value = row?.name;
  voicemailToEditOrDelete.value = row;
};

const handleDeleteClick = (row) => {
  showDeleteModal.value = true;
  voicemailToEditOrDelete.value = row;
};

const handleFetchVoicemails = async () => {
  loadingTableData.value = true;
  voicemails.value = await toFetchVoicemails();
  if (voicemails.value) loadingTableData.value = false;
};

const toUpdateVoicemail = async () => {
  try {
    const payload = {
      name: editableName.value,
    };

    const response = await CallService.updateVoicemail(
      voicemailToEditOrDelete.value?.id,
      payload,
    );

    if (response?.success) {
      showEditModal.value = false;
      await handleFetchVoicemails();
      message.success("Voicemail has been successfully updated!");
    }
  } catch (error) {
    console.error("An error occurred while updating voicemail: ", error);
    message.error("An error occurred while updating voicemail: ");
  }
};

const toDeleteVoicemail = async () => {
  try {
    const response = await CallService.deleteVoicemail(
      voicemailToEditOrDelete.value?.id,
    );
    if (response?.success) {
      showDeleteModal.value = false;
      await handleFetchVoicemails();
      message.success("Voicemail has been successfully deleted!");
    }
  } catch (error) {
    console.error("An error occurred while deleting voicemail: ", error);
    message.error("An error occurred while deleting voicemail");
  }
};

watch(showEditModal, (newVal) => {
  if (newVal) {
    nextTick(() => {
      if (editVoicemailNameInputRef?.value) {
        editVoicemailNameInputRef.value?.focus();
      }
    });
  }
});

onMounted(() => {
  handleFetchVoicemails();
});
</script>
